import { DoNotInterviewButton } from '../drivers/ApplicantBlockButtons'
import { canStopInterviewing } from '../../utils/pipelineStages'
import {
  Mail,
  Phone,
  MessageCircle,
  MapPin,
  Building2,
  Star,
  Award,
  FileText,
  ExternalLink,
  AlertTriangle,
  AlertCircle,
  UserRound,
  ClipboardList,
} from 'lucide-react'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from './Chip'
import { GateChips } from './GateChips'
import { ScreeningChips } from './ScreeningChips'
import { PrdpCardButton } from './PrdpCardButton'
import { BoardLinkButton } from '../drivers/BoardLinkButton'
import { statusReasonText } from '../../utils/screening'

// The reason written with the last status change. An application the old
// automatic score closed carries no written reason, and says so rather than
// showing nothing.
function closedWhy(app: SelectedApplication): string | null {
  const said = statusReasonText(app.status_reason as string | null)
  if (said) return said
  if (app.current_status === 'rejected_score')
    return 'Closed by the automatic score. The rule that closed it was not recorded at the time.'
  return null
}
import { EditDriverDialog } from './EditDriverDialog'
import { DriverManagerPanel } from '../managers/DriverManagerPanel'
import { StageControl } from './StageControl'
import { DriverStateControl } from './DriverStateControl'
import { IncidentsDialog } from './IncidentsDialog'
import { DriverPerformancePanel } from './DriverPerformancePanel'
import { BankDetailsPanel } from '../drivers/BankDetailsPanel'
import { DriverDocumentsPanel } from '../drivers/DriverDocumentsPanel'
import { OnboardingConfirmationsPanel } from '../drivers/OnboardingConfirmationsPanel'
import { KnowYourDriverPanel } from '../drivers/KnowYourDriverPanel'
import { ContractPanel } from './ContractPanel'
import { DriverAgreementsPanel } from '../drivers/DriverAgreementsPanel'
import { InterviewScoringPanel } from './InterviewScoringPanel'
import { PipelineCommentsPanel } from './PipelineCommentsPanel'
import { InterviewCard, type OutcomeAction } from './InterviewCard'
import {
  type SelectedApplication,
  type InterviewRow,
  statusLabel,
  statusTone,
  normalizeDocLinks,
  applicantAnswers,
  emailTemplateLabel,
  formatDate,
} from '../../utils/interviews'
import { DriverFinesPanel } from '../fines/PersonFines'
import { DriverOffboardingPanel } from '../offboarding/DriverOffboardingPanel'

function Field({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Mail
  label: string
  value: string | null | undefined
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate text-sm text-foreground">{value || '—'}</p>
      </div>
    </div>
  )
}

export function ApplicationDetail({
  app,
  appLoading,
  appError,
  interviews,
  interviewsLoading,
  interviewsError,
  canAct,
  isViewer,
  managerLinked,
  managerLoading,
  busyInterviewId,
  onSubmit,
  canEditDrivers,
  onDriverSaved,
  isActiveTab = false,
  boardDriverId = null,
}: {
  app: SelectedApplication | null
  appLoading: boolean
  appError: string | null
  interviews: InterviewRow[]
  interviewsLoading: boolean
  interviewsError: string | null
  canAct: boolean
  isViewer: boolean
  managerLinked: boolean
  managerLoading: boolean
  busyInterviewId: string | null
  onSubmit: (interviewId: string, action: OutcomeAction, notes: string | null) => Promise<void>
  canEditDrivers: boolean
  onDriverSaved: () => void
  isActiveTab?: boolean
  /** The driver's id on Active Drivers, so staff can make his board sign-in link here. */
  boardDriverId?: string | null
}) {
  void isActiveTab
  if (appError) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{appError}</span>
        </div>
      </div>
    )
  }

  if (!app && appLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading applicant…
      </div>
    )
  }

  if (!app) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
        <UserRound className="h-10 w-10 opacity-40" />
        <div>
          <p className="text-sm font-medium text-foreground">No applicant selected</p>
          <p className="text-sm">Choose an applicant from the list to review and score their interview.</p>
        </div>
      </div>
    )
  }

  const docLinks = normalizeDocLinks(app.document_links)
  const docAnswers = applicantAnswers(app.raw_payload)
  const isActiveDriver = ['active', 'offboarding', 'compliance', 'suspended', 'dismissed', 'resigned'].includes(app.current_status)

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-5">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-foreground">{app.full_name || 'Unnamed applicant'}</h2>
            <div className="mt-1.5">
              <Chip tone={statusTone(app.current_status)}>{statusLabel(app.current_status)}</Chip>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">
                {app.last_email_sent_at
                  ? `${emailTemplateLabel(app.last_email_template_key)} · ${formatDate(app.last_email_sent_at)}`
                  : 'No email sent since migration'}
              </span>
            </div>
            {closedWhy(app) && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Why it is here: </span>
                {closedWhy(app)}
                {app.status_reason && app.status_changed_at ? ` (${formatDate(app.status_changed_at as string)})` : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {canEditDrivers && <EditDriverDialog app={app} onSaved={onDriverSaved} />}
            {canEditDrivers && canStopInterviewing(app.current_status) && (
              <DoNotInterviewButton applicationId={app.application_id} name={app.full_name} onDone={onDriverSaved} />
            )}
            <div className="rounded-lg border border-border bg-card px-4 py-2 text-center">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Score</p>
              <p className="text-2xl font-bold tabular-nums text-[hsl(var(--brand))]">{app.score ?? '—'}</p>
            </div>
          </div>
        </div>

        {/* Pipeline stage, or active-driver tools for a live driver */}
        {isActiveDriver ? (
          <div className="space-y-3">
            <DriverStateControl
              applicationId={app.application_id}
              currentStatus={app.current_status}
              canAct={canAct}
              canEditDrivers={canEditDrivers}
              onSaved={onDriverSaved}
            />
            <DriverManagerPanel
              applicationId={app.application_id}
              canEdit={canEditDrivers}
            />
            {/* Platform Request #34: no driver had ever been connected to his board,
                because the only sign-in link button was on the Competition page. */}
            {canEditDrivers && boardDriverId && (
              <div className="rounded-lg border border-border bg-card p-3">
                <p className="mb-2 text-xs text-muted-foreground">
                  Driver board: a driver connects his Google account by opening this link on his phone. Send it on WhatsApp.
                </p>
                <BoardLinkButton driverId={boardDriverId} />
              </div>
            )}
            <IncidentsDialog applicationId={app.application_id} canAct={canAct} />
            {/* Renders nothing unless the login holds can_manage_fines. */}
            <DriverFinesPanel applicationId={app.application_id} />
            <DriverPerformancePanel applicationId={app.application_id} />
            {/* Bank details sit with the active-driver tools, not with Documents.
                A confirmation letter is not a document to file, it is the only
                thing that can set an account, and the account has to be visible
                beside the person it pays. */}
            <BankDetailsPanel applicationId={app.application_id} />
            {/* Documents sit HERE, with the active-driver tools, on Luyanda's
                instruction of 27 August 2026: "I want them to sit like the bank
                and contract". They were below the eligibility gates, which is
                where an applicant's paperwork belongs and not where a working
                driver's identity documents belong. A licence expiry is an
                operational fact about a man who is on the road today. */}
            <DriverDocumentsPanel applicationId={app.application_id} />
          </div>
        ) : (
          <StageControl
            applicationId={app.application_id}
            currentStatus={app.current_status}
            canAct={canAct}
            onSaved={onDriverSaved}
          />
        )}

        {/* Offboarding: the live case, the action that opens one, or for a
            returning applicant how their last case ended and the rehire
            position. Renders nothing when none of those apply. */}
        <DriverOffboardingPanel applicationId={app.application_id} isActiveDriver={isActiveDriver} />

        {/* Employment contract */}
        <ContractPanel applicationId={app.application_id} canAct={canAct} />

        {/* Agreements: the terms in force, every contract with its version,
            Addendum No. 1 and the documents given. Migration 3706. */}
        <DriverAgreementsPanel applicationId={app.application_id} />

        {/* Training and induction, TR-01 and IN-01. Directly under the contract
            because that is the order they happen in: the contract is signed, the
            driver is activated, and these two open the same moment. Shown only
            for a live driver, since activation is what opens them and an
            applicant has none. */}
        {isActiveDriver && (
          <OnboardingConfirmationsPanel applicationId={app.application_id} />
        )}

        {/* The Mackay 66 and the coaching log. Active drivers only: an applicant
            has no manager to coach him and no consent to hold, and the profile is
            about a man who works here. Migration 0125. */}
        {isActiveDriver && <KnowYourDriverPanel applicationId={app.application_id} />}

        {/* Blocked banner */}
        {app.was_blocked && (
          <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="font-semibold">Blocked: </span>
              {app.block_reason || 'This application was blocked.'}
            </span>
          </div>
        )}

        {/* Contact + profile */}
        <section className="rounded-lg border border-border bg-card p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field icon={Mail} label="Email" value={app.primary_email} />
            <Field icon={Phone} label="Phone" value={app.primary_phone} />
            <Field icon={MessageCircle} label="WhatsApp" value={app.whatsapp_phone} />
            <Field icon={MapPin} label="Province" value={app.province} />
            <Field icon={Building2} label="Town" value={app.town} />
            <Field icon={Star} label="Uber rating" value={app.uber_rating_bucket} />
            <Field icon={Award} label="Uber Pro level" value={app.uber_pro_level} />
          </div>
        </section>

        {/* Everything the applicant actually answered on the form, beyond the
            handful of fields already pulled out above into their own columns.
            Read straight from the submission we already hold, raw_payload,
            which this screen's own API call already returns and, until now,
            never rendered. File uploads are left out here on purpose: they are
            shown, and can be turned into a real copy, by the Documents panel
            below. */}
        {docAnswers.length > 0 && (
          <section className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Application answers</h3>
            <dl className="grid gap-3 sm:grid-cols-2">
              {docAnswers.map((qa) => (
                <div key={qa.fieldId} className="min-w-0">
                  <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {qa.question}
                  </dt>
                  <dd className="text-sm text-foreground">{qa.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* What the document reader found on the PrDP and the Uber profile.
            Sits above the gates because a PrDP is mandatory and the gates are not. */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-foreground">PrDP and Uber check</h3>
          <ScreeningChips app={app} />
          {canEditDrivers && app.prdp_document_type === 'temporary' && app.prdp_verdict === 'valid' && !app.prdp_card_received_at && (
            <PrdpCardButton
              applicationId={app.application_id}
              validUntil={app.prdp_valid_until}
              onSaved={onDriverSaved}
            />
          )}
        </section>

        {/* Gates */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-foreground">Eligibility gates</h3>
          <GateChips app={app} />
        </section>

        {/* An applicant who is not yet a driver still has documents, and they
            still have to be read. The same panel, one level down the page,
            because for an applicant the paperwork is part of assessment rather
            than part of running a car. */}
        {!isActiveDriver && <DriverDocumentsPanel applicationId={app.application_id} />}

        {/* The application form's own attachments, kept and clearly labelled as
            what they are rather than deleted. These are the PrDP copy and the two
            Uber screenshots the Driver Employment Form asks for, and they are
            api.typeform.com URLs: THEY NEED A TYPEFORM LOGIN TO OPEN, and they
            die with that account. They stay visible because for 1,127
            applications they are the only trace of what was submitted, and the
            label says plainly what they are worth. Anything that matters gets
            pulled into the panel above as a copy. */}
        {docLinks.length > 0 && (
          <section>
            <h3 className="mb-1 text-sm font-semibold text-foreground">
              Application form attachments
            </h3>
            <p className="mb-2 text-xs text-muted-foreground">
              Links into Typeform, not copies we hold. They open only for someone signed in to
              Typeform.
            </p>
            <ul className="flex flex-col gap-1.5">
              {docLinks.map((doc, i) => (
                <li key={`${doc.url}-${i}`}>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">{doc.label}</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Interview Scoring */}
        <section>
          <div className="mb-3 flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-[hsl(var(--brand))]" />
            <h3 className="text-sm font-semibold text-foreground">Interview Scoring</h3>
          </div>

          {!isViewer && !managerLoading && !managerLinked && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Your login is not linked to a Fleetly manager record, so you cannot record outcomes.</span>
            </div>
          )}

          {/* The scored rubric, driver_rubric_v1, migration 0265. The score decides:
              Interview 1 closes itself on submit, and Interview 2 can only record
              what the rule computed. The cards below keep No-show and Reschedule,
              which are facts about the booking, not judgements about the man. */}
          {!isActiveDriver && (
            <div className="mb-4">
              <InterviewScoringPanel applicationId={app.application_id} onOutcomeRecorded={onDriverSaved} />
            </div>
          )}

          {interviewsError ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{interviewsError}</span>
            </div>
          ) : interviewsLoading && interviews.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" /> Loading interviews…
            </div>
          ) : interviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">No interviews have been scheduled yet.</p>
          ) : (
            <div className="space-y-3">
              {interviews.map((iv) => (
                <InterviewCard
                  key={iv.id}
                  interview={iv}
                  applicantName={app.full_name || 'this applicant'}
                  canAct={canAct}
                  busy={busyInterviewId === iv.id}
                  onSubmit={onSubmit}
                />
              ))}
            </div>
          )}
        </section>

        {/* Xolile's Driver Pipeline comment feature, migration 0391 (renamed from 0344).
            Visible to anyone with canView; only canCommentOnPipeline
            (Xolile today) sees the input box. See PipelineCommentsPanel. */}
        <PipelineCommentsPanel applicationId={app.application_id} />
      </div>
    </div>
  )
}
